import * as Api from '@ViewModels';

/**
 * Utility functions for filtering email campaigns
 */

/**
 * Checks if a campaign should be hidden based on cancelled status criteria
 * @param campaign - The campaign to check
 * @param isExplicitlyCancelledFilter - Whether the user has explicitly selected "Cancelled" filter
 * @returns true if the campaign should be hidden, false otherwise
 */
export function shouldHideCancelledCampaign(campaign: Api.ICampaign, isExplicitlyCancelledFilter = false): boolean {
	// If user explicitly selected "Cancelled" filter, show all cancelled campaigns
	if (isExplicitlyCancelledFilter) {
		return false;
	}

	// Only apply hiding logic to cancelled campaigns
	if (campaign.status !== Api.EmailSendStatus.Cancelled) {
		return false;
	}

	// Check if campaign has no send activity (no emails sent)
	const hasNoSendActivity = !campaign.sentSuccessfullyCount || campaign.sentSuccessfullyCount === 0;

	// Check if campaign is more than 30 days old
	// Use cancelledDate if available (more accurate), otherwise fall back to creationDate
	const dateToCheck = campaign.cancelledDate || campaign.creationDate;
	const isMoreThan30DaysOld = isOlderThan30Days(dateToCheck);

	// Hide if all criteria are met: cancelled, no send activity, and older than 30 days
	return hasNoSendActivity && isMoreThan30DaysOld;
}

/**
 * Checks if a date is more than 30 days old
 * @param dateValue - The date to check. Can be a Date object, ISO string, or undefined/null
 * @returns true if the date is more than 30 days old, false otherwise (including when date is invalid/missing)
 */
export function isOlderThan30Days(dateValue?: string | Date | null): boolean {
	if (!dateValue) {
		return false;
	}

	const date = new Date(dateValue);

	// Check if the date is valid
	if (Number.isNaN(date.getTime())) {
		return false;
	}

	const thirtyDaysAgo = new Date();
	thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

	return date < thirtyDaysAgo;
}

/**
 * Filters out campaigns that should be hidden based on cancelled status criteria
 * @param campaigns - Array of campaigns to filter
 * @param isExplicitlyCancelledFilter - Whether the user has explicitly selected "Cancelled" filter
 * @returns Filtered array of campaigns
 */
export function filterCancelledCampaigns(
	campaigns: Api.ICampaign[],
	isExplicitlyCancelledFilter = false
): Api.ICampaign[] {
	return campaigns.filter(campaign => !shouldHideCancelledCampaign(campaign, isExplicitlyCancelledFilter));
}
